import './addPairModal.scss';
import 'fields/SelectExchangeField/style.scss';

import { BaseMultiSelectField } from 'fields/base/BaseMultiSelectField';
import { InputField } from 'fields/base/InputField/InputField';
import { InsertButton } from 'fields/components';
import { ExchangeOption } from 'fields/ExchangeOption';
import { motion } from 'framer-motion';
import { useCallback, useMemo } from 'react';

import { IDex } from 'api/apiDictionary/models';
import {
  AERODROM_FEES,
  AERODROME_V3_2_FEES,
  HAQQ_FEES,
  IFee,
  PANCAKE_FEES,
  UNISWAP_FEES,
} from 'shared/consts/fees';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { Modal } from 'ui/Modal/Modal';
import { SmallRowListSelector } from 'ui/SmallRowListSelector/SmallRowListSelector';
import { Spinner } from 'ui/Spinner/Spinner';
import { isAddress } from 'utils';
import { ECexName, EExchange } from 'web3';

import { useAddPairModal } from './useAddPairModal/useAddPairModal';

import { PairIcon } from 'assets/icons';

const formatV3Fee = (fee: number): string => `${fee / 10_000}%`;

type AddPairModalProps = {
  onClose: () => void;
  onOpen: () => void;
  projectId: number;
  tradingType: EExchange;
  dex?: IDex;
};

export const AddPairModal = ({
  onClose,
  onOpen,
  projectId,
  tradingType,
  dex,
}: AddPairModalProps) => {
  const {
    cexs,
    values,
    errors,
    touched,
    setFormField,
    handleSubmit,
    loading,
    pairParsingLoading,
    pairParsingError,
    edited,
    isPancakeInfinite,
    resolvedPool,
  } = useAddPairModal({
    projectId,
    onClose,
    onOpen,
    tradingType,
    dex,
  });

  const feesAreVisible = useMemo(
    () =>
      dex &&
      (dex.dex === 'uniswap_v3' ||
        dex.dex === 'uniswap_v3:arbitrum_one' ||
        dex.dex === 'uniswap_v3:polygon' ||
        dex.dex === 'uniswap_v3:base' ||
        dex.dex === 'uniswap_v3:robinhood' ||
        dex.dex === 'pancakeswap_v3:bsc' ||
        dex.dex === 'pancakeswap_v3:robinhood' ||
        dex.dex === 'sushiswap_v3:haqq' ||
        dex.dex === 'aerodrome_v3:base' ||
        dex.dex === 'aerodrome_v3_2:base'),
    [dex],
  );
  const isAerodrome = dex?.dex === 'aerodrome_v3:base' || dex?.dex === 'aerodrome_v3_2:base';
  const hasPoolAddress = isAddress(values.pairAddress);
  const resolvedTickSpacing = resolvedPool?.extra?.tickSpacing;
  const showResolvedPoolFee = Boolean(
    resolvedPool && resolvedPool.addr.toLowerCase() === values.pairAddress.toLowerCase(),
  );
  const showFeePanel = feesAreVisible && (!hasPoolAddress || showResolvedPoolFee);
  const integrationVersionOptions = useMemo(
    () =>
      (dex?.integration_versions ?? ['legacy']).map(value => ({
        label: value === 'v2' ? 'V2 Full' : 'Legacy',
        value,
      })),
    [dex?.integration_versions],
  );

  const feesList = useMemo(() => {
    if (dex && dex.dex === 'aerodrome_v3_2:base') {
      return AERODROME_V3_2_FEES;
    }

    if (dex && ['aerodrome_v3:base'].includes(dex.dex)) {
      return AERODROM_FEES;
    }

    if (
      dex &&
      [
        'uniswap_v3',
        'uniswap_v3:arbitrum_one',
        'uniswap_v3:polygon',
        'uniswap_v3:base',
        'uniswap_v3:robinhood',
      ].includes(dex.dex)
    )
      return UNISWAP_FEES;

    if (dex && ['pancakeswap_v3:bsc', 'pancakeswap_v3:robinhood'].includes(dex.dex))
      return PANCAKE_FEES;

    if (dex && ['sushiswap_v3:haqq'].includes(dex.dex)) return HAQQ_FEES;

    return [];
  }, [dex]);

  const handleSwapTokens = useCallback(() => {
    const baseToken = values.baseToken;
    const quoteToken = values.quoteToken;

    setFormField('baseToken', quoteToken);
    setFormField('quoteToken', baseToken);
  }, [values, setFormField]);

  const cexsFormatted = useMemo(() => cexs?.map(el => ({ ...el, id: el.cex })) ?? [], [cexs]);

  const selectedCexsText = useMemo(
    () => values.cexs.map(el => el.description).join(', '),
    [values.cexs],
  );

  const renderCexItem = useCallback(
    ({ cex, description }: (typeof cexsFormatted)[number]) => {
      const isItemSelected = values.cexs.some(el => el.cex === cex);

      return <ExchangeOption exchange={cex} label={description} selected={isItemSelected} />;
    },
    [values.cexs],
  );

  return (
    <Modal
      title="New trading pair"
      edited={edited}
      onClose={onClose}
      customButtons={
        loading || pairParsingLoading ? (
          <ButtonLoading />
        ) : (
          <Button type="button" onClick={() => handleSubmit()}>
            Create
          </Button>
        )
      }
    >
      <div className="mm-add-pair-modal">
        {tradingType === EExchange.dex && integrationVersionOptions.length > 1 && (
          <div className="mm-add-pair-modal__fee-selector">
            <span className="mm-add-pair-modal__fee-selector__text">Integration version:</span>
            <SmallRowListSelector<(typeof integrationVersionOptions)[number]>
              list={integrationVersionOptions}
              selected={integrationVersionOptions.find(
                option => option.value === values.integrationVersion,
              )}
              field="label"
              onSelect={option => setFormField('integrationVersion', option.value)}
            />
          </div>
        )}
        {tradingType === EExchange.dex && !isPancakeInfinite && (
          <InputField
            label="Pair address(optional)"
            type="text"
            value={values.pairAddress}
            setValue={newPairAddress => setFormField('pairAddress', newPairAddress)}
            nodeRight={
              <InsertButton
                onInsert={newPairAddress => setFormField('pairAddress', newPairAddress)}
              />
            }
            errorMessage={
              pairParsingError
                ? pairParsingError
                : errors.pairAddress && touched.pairAddress
                  ? errors.pairAddress
                  : undefined
            }
          />
        )}
        {tradingType === EExchange.dex && isPancakeInfinite && (
          <InputField
            label="Pool ID"
            type="text"
            value={values.poolId}
            setValue={newPoolId => setFormField('poolId', newPoolId)}
            nodeRight={<InsertButton onInsert={newPoolId => setFormField('poolId', newPoolId)} />}
            errorMessage={
              pairParsingError
                ? 'Invalid pool ID or contract error'
                : errors.poolId && values.poolId.length > 0
                  ? errors.poolId
                  : undefined
            }
          />
        )}
        <motion.div className="mm-add-pair-modal__token-selector">
          <div className="mm-add-pair-modal__token-selector__pair">
            {tradingType === EExchange.cex && cexs && (
              <BaseMultiSelectField
                label="Exchange"
                items={cexsFormatted}
                selectedItems={values.cexs}
                selectedItemsText={selectedCexsText}
                disabled={loading}
                dropdownMaxHeight={280}
                renderItem={renderCexItem}
                onSelectItems={newItems => setFormField('cexs', newItems)}
                errorMessage={errors.cexs ? String(errors.cexs) : undefined}
              />
            )}

            {values.cexs.some(cex => cex.cex === ECexName.mexc) && (
              <div className="w-full text-red-500 text-sm mt-3">
                High risk tokens can be specified with the prefix: @
              </div>
            )}

            <InputField
              label="Token base"
              disabled={pairParsingLoading || (isPancakeInfinite && values.poolId.length > 0)}
              type="text"
              value={values.baseToken}
              setValue={newBaseToken =>
                setFormField(
                  'baseToken',
                  tradingType === EExchange.cex ? newBaseToken.toUpperCase() : newBaseToken,
                )
              }
              nodeRight={
                <InsertButton
                  onInsert={newBaseToken =>
                    setFormField(
                      'baseToken',
                      tradingType === EExchange.cex ? newBaseToken.toUpperCase() : newBaseToken,
                    )
                  }
                />
              }
              errorMessage={errors.baseToken && touched.baseToken ? errors.baseToken : undefined}
            />
            <motion.button
              whileHover={{ scale: 1.2 }}
              className="swap-tokens-button"
              onClick={handleSwapTokens}
            >
              {pairParsingLoading && <Spinner size="mini" />}
              {!pairParsingLoading && <PairIcon />}
            </motion.button>
            <InputField
              label="Token quote"
              disabled={pairParsingLoading || (isPancakeInfinite && values.poolId.length > 0)}
              type="text"
              value={values.quoteToken}
              setValue={newQuoteToken =>
                setFormField(
                  'quoteToken',
                  tradingType === EExchange.cex ? newQuoteToken.toUpperCase() : newQuoteToken,
                )
              }
              nodeRight={
                <InsertButton
                  onInsert={newQuoteToken =>
                    setFormField(
                      'quoteToken',
                      tradingType === EExchange.cex ? newQuoteToken.toUpperCase() : newQuoteToken,
                    )
                  }
                />
              }
              errorMessage={errors.quoteToken && touched.quoteToken ? errors.quoteToken : undefined}
            />
          </div>
        </motion.div>

        {tradingType === EExchange.dex && (
          <motion.div
            initial={{ height: 0 }}
            animate={{
              height: showFeePanel ? 'auto' : 0,
              overflow: showFeePanel ? 'visible' : 'hidden',
            }}
            className="mm-add-pair-modal__fee-selector"
          >
            {showResolvedPoolFee && resolvedPool ? (
              <div className="mm-add-pair-modal__fee-selector__resolved">
                <span>Fee: {formatV3Fee(resolvedPool.fee)}</span>
                {isAerodrome && typeof resolvedTickSpacing === 'number' && (
                  <span>Tick spacing: {resolvedTickSpacing}</span>
                )}
              </div>
            ) : (
              <>
                <span className="mm-add-pair-modal__fee-selector__text">
                  {isAerodrome ? 'Tick spacing' : 'Fee:'}
                </span>
                <SmallRowListSelector<IFee>
                  list={feesList}
                  selected={feesList.find(fee => fee.value === values.fee)}
                  field={'text'}
                  onSelect={newSelectedFee => setFormField('fee', newSelectedFee.value)}
                />
              </>
            )}
          </motion.div>
        )}

        <div className="mm-add-pair-modal__notes">
          <InputField
            type="text"
            label="Notes"
            value={values.notes}
            setValue={newNotes => setFormField('notes', newNotes)}
            errorMessage={errors.notes && touched.notes ? errors.notes : undefined}
          />
        </div>
      </div>
    </Modal>
  );
};
